import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import {
  SignedUrlRequest,
  SignedUrlResponse,
  SignedUrlUploadResult,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class SignedUrlUploadService {
  constructor(private http: HttpClient) {}

  /**
   * Request a signed URL for uploading a file to Firebase Storage
   */
  requestSignedUrl(request: SignedUrlRequest): Observable<SignedUrlResponse> {
    return this.http.post<SignedUrlResponse>('/media/signed-url', request);
  }

  /**
   * Complete flow: request signed URL and upload file
   */
  uploadFile(
    file: File,
    id: string,
    isProfileId = false,
  ): Observable<SignedUrlUploadResult> {
    const timestamp = Date.now();
    const filename = `${timestamp}_${file.name}`;
    if (!this.isValidFile(file)) {
      return throwError(
        () => new Error('Invalid file: file is required and must be valid'),
      );
    }

    const request: SignedUrlRequest = {
      filename,
      ...(isProfileId ? { profileId: id } : { workspaceId: id }),
      fileByteSize: file.size,
      mimeType: file.type,
    };

    return this.requestSignedUrl(request).pipe(
      catchError((error) => {
        this.logError('Failed to request signed URL', error);
        return throwError(
          () => new Error(`Signed URL request failed: ${error.message}`),
        );
      }),
      switchMap((signedUrlResponse) =>
        this.uploadFileWithSignedUrl(file, signedUrlResponse, id),
      ),
    );
  }

  /**
   * Upload a file using a signed URL
   */
  private uploadFileWithSignedUrl(
    file: File,
    signedUrlResponse: SignedUrlResponse,
    id: string,
  ): Observable<SignedUrlUploadResult> {
    return from(this.performSignedUrlUpload(file, signedUrlResponse, id)).pipe(
      catchError((error) => {
        this.logError('File upload failed', error);
        return throwError(() => new Error(`Upload failed: ${error.message}`));
      }),
    );
  }

  /**
   * Validate file
   */
  private isValidFile(file: File): boolean {
    try {
      return Boolean(file && file instanceof File && file.size > 0);
    } catch (error) {
      this.logError('Error validating file', error);
      return false;
    }
  }

  /**
   * Log error with consistent formatting
   */
  private logError(message: string, error: any): void {
    console.error(`${message}:`, error);
  }

  /**
   * Perform the actual upload to the signed URL
   */
  private async performSignedUrlUpload(
    file: File,
    signedUrlResponse: SignedUrlResponse,
    id: string,
  ): Promise<SignedUrlUploadResult> {
    try {
      await this.uploadToSignedUrl(file, signedUrlResponse);

      const dimensions = await this.getFileDimensions(file);

      return this.createUploadResult(
        file,
        signedUrlResponse,
        dimensions,
        signedUrlResponse.downloadURL,
      );
    } catch (error) {
      this.logError('Error during signed URL upload', error);
      throw error;
    }
  }

  /**
   * Create upload result object
   */
  private createUploadResult(
    file: File,
    signedUrlResponse: SignedUrlResponse,
    dimensions: { width: number; height: number },
    thumbnailURL: string,
  ): SignedUrlUploadResult {
    try {
      return {
        url: signedUrlResponse.downloadURL,
        name: file.name,
        size: file.size,
        type: file.type,
        width: dimensions.width,
        height: dimensions.height,
        thumbnailURL,
        downloadURL: signedUrlResponse.downloadURL,
      };
    } catch (error) {
      this.logError('Error creating upload result', error);
      throw new Error('Failed to create upload result');
    }
  }

  /**
   * Upload file to signed URL with proper headers
   */
  private async uploadToSignedUrl(
    file: File,
    signedUrlResponse: SignedUrlResponse,
  ): Promise<Response> {
    try {
      if (!this.isValidSignedUrlResponse(signedUrlResponse)) {
        throw new Error('Invalid signed URL response');
      }

      const headers = new Headers();
      headers.set(
        'x-goog-meta-firebaseStorageDownloadTokens',
        signedUrlResponse.uploadHeaders[
          'x-goog-meta-firebaseStorageDownloadTokens'
        ],
      );

      return await fetch(signedUrlResponse.uploadURL, {
        method: 'PUT',
        headers,
        body: file,
      });
    } catch (error) {
      this.logError('Error during upload to signed URL', error);
      throw error;
    }
  }

  /**
   * Validate signed URL response
   */
  private isValidSignedUrlResponse(response: SignedUrlResponse): boolean {
    try {
      return Boolean(
        response &&
          response.uploadURL &&
          response.downloadURL &&
          response.uploadHeaders &&
          response.uploadHeaders['x-goog-meta-firebaseStorageDownloadTokens'],
      );
    } catch (error) {
      this.logError('Error validating signed URL response', error);
      return false;
    }
  }

  /**
   * Get file dimensions based on file type
   */
  private async getFileDimensions(
    file: File,
  ): Promise<{ width: number; height: number }> {
    try {
      if (this.isImageFile(file)) {
        return await this.getImageDimensions(file);
      }

      if (this.isVideoFile(file)) {
        return await this.getVideoDimensions(file);
      }

      return { width: 0, height: 0 };
    } catch (error) {
      this.logError('Error getting file dimensions', error);
      return { width: 0, height: 0 };
    }
  }

  /**
   * Check if file is an image
   */
  private isImageFile(file: File): boolean {
    try {
      return Boolean(file && file.type && file.type.startsWith('image/'));
    } catch (error) {
      this.logError('Error checking if file is image', error);
      return false;
    }
  }

  /**
   * Check if file is a video
   */
  private isVideoFile(file: File): boolean {
    try {
      return Boolean(file && file.type && file.type.startsWith('video/'));
    } catch (error) {
      this.logError('Error checking if file is video', error);
      return false;
    }
  }

  /**
   * Get image dimensions from a file
   */
  private getImageDimensions(
    file: File,
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          const dimensions = {
            width: img.width || 0,
            height: img.height || 0,
          };
          resolve(dimensions);
          this.cleanupImage(img);
        };
        img.onerror = (error) => {
          this.logError(
            'Image loading error during dimension calculation',
            error,
          );
          resolve({ width: 0, height: 0 });
          this.cleanupImage(img);
        };
        img.src = URL.createObjectURL(file);
      } catch (error) {
        this.logError('Error creating image for dimension calculation', error);
        resolve({ width: 0, height: 0 });
      }
    });
  }

  /**
   * Get video dimensions from a file
   */
  private getVideoDimensions(
    file: File,
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      try {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const dimensions = {
            width: video.videoWidth || 0,
            height: video.videoHeight || 0,
          };
          resolve(dimensions);
          this.cleanupVideo(video);
        };
        video.onerror = (error) => {
          this.logError(
            'Video loading error during dimension calculation',
            error,
          );
          resolve({ width: 0, height: 0 });
          this.cleanupVideo(video);
        };
        video.src = URL.createObjectURL(file);
      } catch (error) {
        this.logError('Error creating video for dimension calculation', error);
        resolve({ width: 0, height: 0 });
      }
    });
  }

  /**
   * Clean up video element and revoke object URL
   */
  private cleanupVideo(video: HTMLVideoElement): void {
    try {
      if (video.src && video.src.startsWith('blob:')) {
        URL.revokeObjectURL(video.src);
      }
    } catch (error) {
      this.logError('Error cleaning up video element', error);
    }
  }

  /**
   * Clean up image element and revoke object URL
   */
  private cleanupImage(img: HTMLImageElement): void {
    try {
      if (img.src && img.src.startsWith('blob:')) {
        URL.revokeObjectURL(img.src);
      }
    } catch (error) {
      this.logError('Error cleaning up image element', error);
    }
  }
}
